import { FormattedMessage } from 'react-intl';
import { MetricsSectionTitle } from 'components/MetricsSectionTitle';
import { InPlaceErrorPlaceholder } from 'components/errorpages/InPlaceErrorPlaceholder';
import { cn } from 'utils/classNamesTailwind';
import { ReactNode } from 'react';

/* eslint-disable react/jsx-props-no-spreading */
function PageContainer({ children = null, className = '', ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={cn(
        'grid xl:grid-cols-12 grid-cols-6 gap-4 my-8 rounded-xl bg-snow-200 px-4 py-4 justify-self-center w-full flex-shrink-1',
        className
      )}
    >
      {children}
    </div>
  );
}

interface PageHeaderProps {
  children?: ReactNode;
  topRightComponent?: ReactNode;
  className?: string;
  tabs?: ReactNode;
}

function PageHeader({
  children = undefined,
  topRightComponent = undefined,
  tabs = undefined,
  className = '',
}: PageHeaderProps) {
  return (
    <header className={cn('sticky top-16 z-[39] w-full bg-snow-50 border-b px-10 pt-6', className)}>
      <div className={cn('flex w-full justify-between ', tabs ? 'pb-3' : 'pb-8')}>
        <div className="flex flex-wrap max-w-full gap-2 items-center">{children}</div>

        {topRightComponent && <div className="flex flex-row ml-4 h-[40px]">{topRightComponent}</div>}
      </div>

      {tabs && <>{tabs}</>}
    </header>
  );
}

function TileContent({ className = '', children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('h-full w-full px-2 py-2', className)} {...props}>
      {children}
    </div>
  );
}

function Tile({
  children = null,
  title = undefined,
  titleId = undefined,
  additionalTitle = null,
  rightHandTitleComponent = null,
  subtitle = null,
  className = '',
  additionalClassName = '',
  isError = false,
  errorDetails = null,
  contentClassName = '',
  testId = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  title?: React.ReactNode;
  titleId?: string;
  subtitle?: React.ReactNode;
  additionalTitle?: React.ReactNode;
  rightHandTitleComponent?: React.ReactNode;
  additionalClassName?: string;
  isError?: boolean;
  errorDetails?: string | null;
  contentClassName?: string;
  testId?: string;
  'data-testid'?: string;
}) {
  const showError = isError || (errorDetails != null && children == null);
  const resolvedTestId = testId ?? dataTestId;
  return (
    <div
      {...props}
      data-testid={!showError ? resolvedTestId : undefined}
      className={cn(
        'bg-white flex flex-col h-full rounded-lg py-4 px-8 col-span-6 xl:col-span-12',
        props.onClick
          ? 'group/tile cursor-pointer hover:shadow-elevation-medium hover:bg-denim-50 transition duration-200 '
          : '',
        className
      )}
    >
      {(!!title || !!titleId || !!additionalTitle || !!subtitle) && (
        <div className={cn('flex items-center bg-white', additionalClassName)}>
          <MetricsSectionTitle additionalTitle={additionalTitle} subtitle={subtitle}>
            {title ?? (titleId ? <FormattedMessage id={titleId} /> : <br />)}
          </MetricsSectionTitle>
          {rightHandTitleComponent}
        </div>
      )}
      {showError ? (
        <div className={cn('flex min-h-[260px] items-center justify-center', contentClassName)}>
          <InPlaceErrorPlaceholder errorDetails={errorDetails} compact testId={resolvedTestId} />
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function PageSectionTitle({
  titleId = undefined,
  additionalTitleId = undefined,
  children = undefined,
  additionalTitle = null,
  subtitle = null,
  subtitleId = undefined,
  rightComponent = null,
  testId = undefined,
  className = '',
}: React.PropsWithChildren<{
  titleId?: string;
  additionalTitleId?: string;
  children?: React.ReactNode;
  additionalTitle?: React.ReactNode;
  subtitle?: React.ReactNode;
  subtitleId?: string;
  rightComponent?: React.ReactNode;
  testId?: string;
  className?: string;
}>) {
  return (
    <Tile className={cn('px-4 py-4', className)} testId={testId}>
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-4">
          <div className="flex flex-row items-center">
            <div className="inline-block space-x-3">
              <span className="text-2xl font-bold">{titleId ? <FormattedMessage id={titleId} /> : children}</span>{' '}
              <span className="text-xs">
                {additionalTitleId ? <FormattedMessage id={additionalTitleId} /> : additionalTitle}
              </span>
            </div>
          </div>
          {subtitle || subtitleId ? (
            <span className="text-sm pt-0.5">{subtitleId ? <FormattedMessage id={subtitleId} /> : subtitle}</span>
          ) : null}
        </div>
        {rightComponent}
      </div>
    </Tile>
  );
}

export { PageHeader, PageContainer, Tile, TileContent, PageSectionTitle };
